import React, { useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

export const METRIC_CONFIG = {
  'Sedan': {
    label: 'Sedan',
    color: '#07A95B', // Emerald green
    fallbackKey: 'totalSedans',
  },
  'SUV': {
    label: 'SUV',
    color: '#2563EB', // Royal blue
    fallbackKey: 'totalSuvs',
  },
  'EV Sedan': {
    label: 'EV Sedan',
    color: '#0284C7', // Sky Blue
  },
  'EV SUV': {
    label: 'EV SUV',
    color: '#8B5CF6', // Purple
  },
  '12 Seater': {
    label: '12 Seater',
    color: '#D97706', // Amber
  },
  '22 Seater': {
    label: '22 Seater',
    color: '#EC4899', // Pink
  },
  '28 Seater': {
    label: '28 Seater',
    color: '#059669', // Teal / Mint
  },
  '33 Seater': {
    label: '33 Seater',
    color: '#6366F1', // Indigo
  },
  '38 Seater': {
    label: '38 Seater',
    color: '#0D9488', // Dark Teal
  },
  '40 Seater': {
    label: '40 Seater',
    color: '#EA580C', // Orange
  },
  '50 Seater': {
    label: '50 Seater',
    color: '#DC2626', // Crimson Red
  },
  'totalClients': {
    label: 'Total Clients',
    color: '#4B5563', // Slate Grey
  },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.06)',
          minWidth: '180px',
          maxWidth: '320px',
        }}
      >
        <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 700, color: '#111827' }}>
          {label}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {payload.map((item) => {
            const config = METRIC_CONFIG[item.dataKey] || {};
            return (
              <div
                key={item.dataKey}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  fontSize: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: '#4B5563', fontWeight: 500 }}>
                    {config.label || item.name}
                  </span>
                </div>
                <span style={{ fontWeight: 700, color: '#111827' }}>
                  {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

const FleetDistributionChart = ({ data = [], loading = false }) => {
  const [visibleLines, setVisibleLines] = useState(() => {
    const initial = {};
    Object.keys(METRIC_CONFIG).forEach((key) => {
      initial[key] = true;
    });
    return initial;
  });

  const toggleLine = (key) => {
    setVisibleLines((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const selectAll = () => {
    const all = {};
    Object.keys(METRIC_CONFIG).forEach((key) => {
      all[key] = true;
    });
    setVisibleLines(all);
  };

  const deselectAll = () => {
    const none = {};
    Object.keys(METRIC_CONFIG).forEach((key) => {
      none[key] = false;
    });
    setVisibleLines(none);
  };

  const chartData = (Array.isArray(data) && data.length > 0 ? data : []).map((item) => {
    const copy = { ...item };
    Object.entries(METRIC_CONFIG).forEach(([key, config]) => {
      if (copy[key] === undefined && config.fallbackKey && copy[config.fallbackKey] !== undefined) {
        copy[key] = copy[config.fallbackKey];
      }
    });
    return copy;
  });

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        padding: '24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {/* Header with Title & Legend Toggles */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Vehicle Types & Client Growth
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6B7280' }}>
              Monthly distribution and growth across all vehicle types and Active Clients
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={selectAll}
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#227B48',
                backgroundColor: '#E8F5EE',
                border: '1px solid #227B48',
                borderRadius: '6px',
                padding: '4px 10px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Select All
            </button>
            <button
              type="button"
              onClick={deselectAll}
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6B7280',
                backgroundColor: '#F3F4F6',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                padding: '4px 10px',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Interactive Clickable Badges to Toggle Lines */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          {Object.entries(METRIC_CONFIG).map(([key, config]) => {
            const isVisible = visibleLines[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleLine(key)}
                style={{
                  border: isVisible ? `1px solid ${config.color}` : '1px solid #E5E7EB',
                  backgroundColor: isVisible ? `${config.color}14` : '#F9FAFB',
                  color: isVisible ? config.color : '#9CA3AF',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                  opacity: isVisible ? 1 : 0.6,
                }}
                title={`Click to ${isVisible ? 'hide' : 'show'} ${config.label}`}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: isVisible ? config.color : '#D1D5DB',
                  }}
                />
                <span>{config.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart Canvas */}
      <div style={{ width: '100%', height: '320px', position: 'relative' }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF',
              fontSize: '14px',
            }}
          >
            Loading trend data...
          </div>
        ) : chartData.length === 0 ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF',
              fontSize: '14px',
            }}
          >
            No trend data available for this range
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
              <XAxis
                dataKey="month"
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 12, fontWeight: 500 }}
                dy={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />

              {Object.entries(METRIC_CONFIG).map(([key, config]) => {
                if (!visibleLines[key]) return null;
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={config.label}
                    stroke={config.color}
                    strokeWidth={key === 'totalClients' ? 2 : 2.5}
                    dot={{ r: 3, stroke: config.color, strokeWidth: 2, fill: '#FFFFFF' }}
                    activeDot={{ r: 6, stroke: '#FFFFFF', strokeWidth: 2, fill: config.color }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default FleetDistributionChart;
